/**
 * Card das bicicletas NOVAS.
 *
 * Porte de `bicicletas_peniel_prototipo.html` linhas 153-171:
 *   bg-white rounded-2xl shadow-sm→shadow-xs hover:shadow-xl
 *   imagem h-64, badge "NOVA" pill, título text-xl, preço text-2xl,
 *   botão WhatsApp verde rounded-xl py-3
 *
 * Tradução v3→v4 aplicada: `shadow-sm`→`shadow-xs`, `flex-grow`→`grow`.
 */
import { Link } from 'react-router-dom'
import type { ProdutoResumo } from '@peniel/shared'
import { SIZES, ehNegociavel } from '@peniel/shared'

import { ImagemResponsiva } from '@/shared/ui/ImagemResponsiva.jsx'
import { BadgeCondicao, BadgeVendida } from './BadgeCondicao.jsx'
import { PrecoProduto } from './PrecoProduto.jsx'
import { BotaoWhatsAppProduto } from './BotaoWhatsApp.jsx'

export function CardProdutoNovo({
  produto,
  prioridade = false,
}: {
  produto: ProdutoResumo
  prioridade?: boolean
}) {
  const vendida = produto.status === 'VENDIDA'
  const negociavel = ehNegociavel(produto.status)

  return (
    <li className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-xs transition-shadow duration-300 hover:shadow-xl">
      <div className="relative h-64 shrink-0">
        <BadgeCondicao condicao={produto.condicao} variante="nova" />
        {vendida && <BadgeVendida />}

        <Link to={`/bicicletas/${produto.slug}`} className="block size-full">
          <ImagemResponsiva
            imagem={produto.imagem}
            alt={produto.nome}
            sizes={SIZES.cardNova}
            className="size-full"
            prioridade={prioridade}
            // Sem zoom quando vendida: o protótipo remove a classe de zoom
            // justamente do card indisponível.
            zoomNoHover={!vendida}
            esmaecida={vendida}
          />
        </Link>
      </div>

      <div className="flex grow flex-col p-6">
        <h3 className="mb-2 text-xl font-bold text-gray-900">
          <Link to={`/bicicletas/${produto.slug}`} className="hover:text-brand-700">
            {produto.nome}
          </Link>
        </h3>

        {produto.resumo && (
          <p className="linhas-2 mb-4 text-sm text-gray-500">{produto.resumo}</p>
        )}

        <div className="mt-auto">
          <PrecoProduto
            centavos={produto.precoCentavos}
            riscado={vendida}
            // `text-gray-500` e não `gray-400`: sobre bg-white o 400 reprova o
            // contraste AA (~2.9:1), e o efeito "apagado" já vem da imagem.
            className={`mb-4 text-2xl font-bold ${vendida ? 'text-gray-500' : 'text-gray-900'}`}
          />

          {negociavel ? (
            <BotaoWhatsAppProduto
              produto={produto}
              variante="whatsapp"
              forma="caixa"
              tamanho="md"
              rotulo="Comprar via WhatsApp"
              className="py-3"
            />
          ) : (
            <IndisponivelBotao />
          )}
        </div>
      </div>
    </li>
  )
}

/**
 * Estado indisponível.
 *
 * O protótipo usava `<button disabled>`. Um `<span>` é melhor: botão
 * desabilitado sai da ordem de tabulação e não é anunciado, então quem usa
 * leitor de tela não descobre que a bike foi vendida. Aqui o texto diz.
 */
export function IndisponivelBotao({ className = '' }: { className?: string }) {
  return (
    <span
      className={`flex min-h-11 w-full items-center justify-center rounded-xl bg-gray-200 px-4 font-semibold text-gray-500 ${className}`}
    >
      Vendida — indisponível
    </span>
  )
}
